import React, { useEffect, useRef } from "react";
import Message from "./Message";
import LoadingDot from './LoadingDot';
import OGFleetIcon from '../../../../assets/OGFLEET Logo.png';
import { Droplet, Database, Fuel, ArrowRight, Settings } from 'lucide-react';

const Chatbox = ({ messages, loading, isDarkTheme, currentConversationId, activeConversationId, onModelSelect, selectedModel  }) => {
  const chatboxRef = useRef(null);

  const modelButtons = [
    { id: 1, name: "OG-Drilling", icon: Droplet },
    { id: 2, name: "OG-Reservoir", icon: Database },
    { id: 3, name: "OG-Production", icon: Fuel }
  ];

  const scrollToBottom = () => {
    if (chatboxRef.current) {
      chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]); // Reacts to changes in `messages`

  useEffect(() => {
    scrollToBottom();
  }, [loading]);

  const shouldShowLoading = loading && currentConversationId === activeConversationId;

  return (
    <div
      className={`chatbox flex-1 flex flex-col p-2 mb-10 mt-2 ${isDarkTheme ? 'bg-dark-background' : 'bg-white'} overflow-hidden overflow-y-auto justify-start items-center w-full md:w-[70%] mx-auto scroll-smooth rounded-lg`}
      ref={chatboxRef}
    >
      {messages.length === 0 ? (
        <div className={`flex flex-col justify-center items-center h-full ${isDarkTheme ? 'text-white' : 'text-gray-800'} text-center p-6`}>
          <div className="flex flex-col items-center space-y-7 max-w-[590px]">
            <div className="p-4 py-0">
              <img
                src={OGFleetIcon}
                alt="OGFLEET Logo"
                className="w-52 h-52 mx-auto rounded-lg -mb-14"
              />
            </div>

            <h2 className={`text-2xl md:text-3xl font-bold ${isDarkTheme ? 'text-white' : 'text-gray-800'}`}>
              Ready to Explore Oil & Gas Solutions?
            </h2>

            <p className={`${isDarkTheme ? 'text-gray-300' : 'text-gray-600'} text-base md:text-lg`}>
              Choose a specialty to start your analysis
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 w-full mt-4">
              {modelButtons.map((model) => (
                <button
                  key={model.id}
                  onClick={() => onModelSelect(model.id, model.name)}
                  className={`flex items-center justify-center space-x-3 ${
                    isDarkTheme 
                      ? 'bg-gray-700 border-amber-500 hover:bg-gray-600' 
                      : 'bg-white border-amber-400 hover:bg-amber-50'
                  } border-2 ${
                    isDarkTheme ? 'text-white' : 'text-gray-800'
                  } font-medium py-3 px-4 rounded-lg transition-all duration-200 group cursor-pointer md:w-full w-60 mx-auto ${
                    selectedModel === model.id ? (isDarkTheme ? 'bg-gray-600' : 'bg-amber-100') : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    <model.icon className="w-5 h-5 text-amber-500 group-hover:scale-110 transition-transform" />
                  </div>
                  <span className="flex-1 break-words text-center">{model.name}</span>
                </button>
              ))}
            </div>

            <div className={`${isDarkTheme ? 'text-gray-400' : 'text-gray-500'} flex items-center mt-6`}>
              <p>Ask your first question after selecting a model</p>
              <ArrowRight className="w-4 h-4 ml-2 animate-pulse" />
            </div>
          </div>
        </div>
      ) : (
        messages.map((msg, index) => (
          msg.sender === "system" && msg.message.includes("You are using the") ? (
            <div
              key={index}
              className="bg-gradient-to-r from-amber-400 to-amber-600 text-white text-center p-4 my-4 rounded-lg w-[90%] md:w-[70%] lg:w-[60%] box-border text-base shadow-lg flex items-center justify-center space-x-3 relative overflow-hidden group"
            >
              <Settings className="w-5 h-5 text-white group-hover:rotate-45 transition-transform duration-300" />
              <span className="text-white font-medium">{msg.message}</span>
              <div className="absolute inset-0 rounded-lg pointer-events-none"></div>
            </div>
          ) : (
            <Message key={index} messageId={msg.message_id} content={msg.message} sender={msg.sender} loading={loading} isDarkTheme={isDarkTheme} />
          )
        ))
      )}
      {/* Show LoadingDot if loading is true */}
      {shouldShowLoading && messages.length > 0  && (
        <div className="flex items-center justify-start w-full pl-10 h-10">
          <LoadingDot />
        </div>
      )}
    </div>
  );
};

export default Chatbox;